import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { FEGeolocation } from '../db/FEGeolocation.js';
import { SmartTeamsAppPermission, SmartTeamsAppUserGroup } from '../features/permissions/roles-and-permissions.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsAppUser } from '../db/SmartTeamsAppUser.js';
import { getCurrentAppUser, isCustomerOfTicket, snAssignedTo } from './_assignee-guard.js';

/**
 * Upsert the current FE's geolocation. Implicitly turns tracking on for the writer
 * — clients only see live position when `isTracking` is true.
 */
export const updateMyGeolocation = registerMutation({
  operationName: 'updateSmartTeamsAppGeolocation',
  type: () => FEGeolocation,
  params: [
    { name: 'latitude', type: () => Float },
    { name: 'longitude', type: () => Float },
    { name: 'accuracy', type: () => Float },
  ],
  resolve:
    (latitude: number, longitude: number, accuracy: number) =>
    async (_gqlFields: BaseQLSelectionSet<FEGeolocation>) => {
      const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketUpdate));
      if (!allowed) throw new Error('Insufficient permissions');

      const userId = (context as any).userID as string | undefined;
      if (!userId) throw new Error('Not authenticated');

      const col = await getRawCollection<FEGeolocation>(FEGeolocation._collectionName);
      const now = new Date().toISOString();

      await col.updateOne(
        { userId },
        { $set: { userId, latitude, longitude, accuracy, updatedAt: now, isTracking: true } },
        { upsert: true },
      );

      return new FEGeolocation({ userId, latitude, longitude, accuracy, updatedAt: now, isTracking: true });
    },
});

/**
 * Explicitly stop tracking. Clears the stored coordinates as well as the `isTracking` flag so
 * the FE's precise last-known position doesn't linger at rest after they opt out — the customer
 * view is already gated on `isTracking`, this is defence in depth for the data at rest.
 */
export const stopMyGeolocationTracking = registerMutation({
  operationName: 'stopSmartTeamsAppGeolocationTracking',
  type: () => Boolean,
  params: [],
  resolve: () => async () => {
    const userId = (context as any).userID as string | undefined;
    if (!userId) throw new Error('Not authenticated');

    const col = await getRawCollection<FEGeolocation>(FEGeolocation._collectionName);
    await col.updateOne(
      { userId },
      { $set: { isTracking: false, latitude: null, longitude: null, accuracy: null } },
    );
    return true;
  },
});

/**
 * Read the FE's live location for a given ticket. Visible to:
 *   - the assigned FE themself
 *   - a customer of the ticket (Customer userGroup with at least one linked customer whose serviceNowClientId matches)
 *   - NSC Admins (read-only oversight)
 *
 * Returns null when the FE is not tracking, or when the caller has no relationship to the ticket.
 */
export const getFEGeolocationForTicket = registerQuery({
  operationName: 'getSmartTeamsAppFEGeolocationForTicket',
  type: () => FEGeolocation,
  params: [{ name: 'ticketNumber', type: () => String }],
  resolve: (ticketNumber: string) => async (_gqlFields: BaseQLSelectionSet<FEGeolocation>) => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketView));
    if (!allowed) throw new Error('Insufficient permissions');

    const appUser = await getCurrentAppUser();
    const { getTicketDetails } = await import('@nsc/servicenow/smartteam-api.js');
    const sn = await getTicketDetails(ticketNumber);
    const assignedToSysId = snAssignedTo(sn)?.value;
    if (!assignedToSysId) return null;

    const ticketClientSysId = sn.customer?.reference?.value;

    const isAdmin = appUser.userGroup === SmartTeamsAppUserGroup.Admin;
    const isAssignedFE =
      appUser.userGroup === SmartTeamsAppUserGroup.FieldEngineer &&
      !!appUser.serviceNowFEId &&
      appUser.serviceNowFEId === assignedToSysId;
    const customerAccess = await isCustomerOfTicket({
      appUser,
      ticketNumber: sn.number?.display_value,
      ticketClientSysId,
    });

    if (!isAdmin && !isAssignedFE && !customerAccess) {
      throw new Error('You do not have access to this ticket');
    }

    // Map FE sys_id back to our app user id, since FEGeolocation is keyed by AdornisUser._id.
    const appUserCol = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
    const feAppUser = await appUserCol.findOne({ serviceNowFEId: assignedToSysId });
    if (!feAppUser?.userId) return null;

    const geoCol = await getRawCollection<FEGeolocation>(FEGeolocation._collectionName);
    const geo = await geoCol.findOne({ userId: feAppUser.userId });
    if (!geo) return null;

    // Hide live position when tracking is off — only the FE themself sees their own row.
    if (!geo.isTracking && !isAssignedFE) return null;

    // A broadcast that dies without an explicit stop (tab closed, asleep, GPS lost) leaves
    // isTracking=true with frozen coords; don't show a stale fix to a customer/admin as "live".
    if (geo.isTracking && !isAssignedFE) {
      const STALE_AFTER_MS = 120_000; // ~4 missed 30s (GEO_UPDATE_INTERVAL) pushes
      const ts = geo.updatedAt ? Date.parse(geo.updatedAt) : Number.NaN;
      if (!Number.isFinite(ts) || Date.now() - ts > STALE_AFTER_MS) return null;
    }

    return new FEGeolocation(geo);
  },
});
